import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyRequests, AccessRequest } from '../api';

const statusColors: Record<string, React.CSSProperties> = {
  pending: { background: '#fff3cd', color: '#856404' },
  approved: { background: '#d4edda', color: '#155724' },
  denied: { background: '#f8d7da', color: '#721c24' },
};

const MyRequests: React.FC = () => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const data = await getMyRequests();
        setRequests(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load requests.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <p style={styles.loading}>Loading your requests...</p>;
  }

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.heading}>My Access Requests</h1>
        <Link to="/request" style={styles.newBtn}>
          + New Request
        </Link>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {requests.length === 0 ? (
        <div style={styles.empty}>
          <p>You have not submitted any access requests yet.</p>
          <Link to="/request" style={styles.linkBtn}>
            Submit your first request
          </Link>
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Scope</th>
                <th style={styles.th}>Reason</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Admin Note</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => (
                <tr key={req.id} style={styles.tr}>
                  <td style={styles.td}>
                    {new Date(req.createdAt).toLocaleDateString()}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.scope}>{req.scope}</span>
                  </td>
                  <td style={{ ...styles.td, maxWidth: 280 }}>
                    {req.reason.length > 80
                      ? req.reason.substring(0, 80) + '...'
                      : req.reason}
                  </td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badge,
                        ...statusColors[req.status],
                      }}
                    >
                      {req.status}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {req.adminNote || '--'}
                  </td>
                  <td style={styles.td}>
                    {req.status === 'approved' && req.mfaComplete && (
                      <span style={styles.mfaDone}>MFA Complete</span>
                    )}
                    {req.status === 'approved' && !req.mfaComplete && (
                      <Link
                        to={`/mfa-setup/${req.id}`}
                        style={styles.actionLink}
                      >
                        Set Up MFA
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  heading: {
    fontSize: '1.6rem',
    fontWeight: 700,
  },
  newBtn: {
    background: '#4a6cf7',
    color: '#fff',
    textDecoration: 'none',
    padding: '0.5rem 1.25rem',
    borderRadius: 6,
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#888',
  },
  errorBanner: {
    background: '#f8d7da',
    color: '#721c24',
    border: '1px solid #f5c6cb',
    borderRadius: 6,
    padding: '0.85rem 1rem',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  empty: {
    textAlign: 'center',
    padding: '3rem',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  linkBtn: {
    color: '#4a6cf7',
    fontWeight: 600,
    textDecoration: 'none',
    marginTop: '0.5rem',
    display: 'inline-block',
  },
  tableWrapper: {
    overflowX: 'auto',
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '0.75rem 1rem',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '2px solid #eee',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '0.75rem 1rem',
    fontSize: '0.9rem',
    verticalAlign: 'top',
  },
  scope: {
    fontFamily: 'monospace',
    fontSize: '0.82rem',
  },
  badge: {
    display: 'inline-block',
    padding: '0.2rem 0.6rem',
    borderRadius: 12,
    fontSize: '0.78rem',
    fontWeight: 600,
    textTransform: 'capitalize',
  },
  actionLink: {
    color: '#4a6cf7',
    fontWeight: 600,
    textDecoration: 'none',
    fontSize: '0.85rem',
  },
  mfaDone: {
    color: '#155724',
    fontWeight: 600,
    fontSize: '0.85rem',
  },
};

export default MyRequests;
